<template>
  <client-only>
    <LMap
      ref="map"
      :center="countryCenterCoords"
      :zoom.sync="zoom"
      :options="mapOptions"
      class="!z-0 !h-[600px]"
      @ready="onMapReady"
    >
      <LTileLayer
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; <a href='http://www.openstreetmap.org/copyright'>OpenStreetMap</a>"
      />

      <LControlScale
        position="bottomright"
        :imperial="false"
        :max-width="150"
      />

      <LPolygon
        v-for="zone in zonesWithStyle"
        :key="`zone-${zone.id}`"
        :lat-lngs="zone.latLng"
        :color="zone.color"
        :fill-color="zone.color"
        @click="onZoneClick(zone)"
      >
        <LTooltip>
          <div class="flex flex-row justify-between gap-2">
            <div class="font-bold">{{ zone.fullName }}</div>
            <div>{{ $n(zone.price, "currency") }}</div>
          </div>
          <div v-if="zone.isRemoved" class="text-xs italic">
            {{ $t("conductor.zones.mapSelector.removedHint") }}
          </div>
          <div v-else-if="zone.isExisting" class="text-xs italic">
            {{ $t("conductor.zones.mapSelector.existingHint") }}
          </div>
          <div v-else-if="zone.isSelected" class="text-xs italic">
            {{ $t("conductor.zones.mapSelector.selectedHint") }}
          </div>
          <div v-else-if="zone.isReference" class="text-xs italic">
            {{ $t(referenceHintKey) }}
          </div>
          <div v-else class="text-xs italic">
            {{ $t("conductor.zones.mapSelector.unselectedHint") }}
          </div>
        </LTooltip>
      </LPolygon>

      <LMarker
        v-if="personCityLatLng"
        :lat-lng="personCityLatLng"
        :options="{ zIndexOffset: 1000 }"
      >
        <LIcon :icon-url="markerIcon" />
        <LTooltip>{{ personCity.name }}</LTooltip>
      </LMarker>

      <div v-if="!zones.length" />
    </LMap>
  </client-only>
</template>

<script>
import { computed, ref } from "@nuxtjs/composition-api";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import { useSettingsStore } from "~/stores/settings";
import "leaflet/dist/leaflet.css";

const ImportLeafletComponent = (componentName) => {
  return () => {
    return process.client
      ? import("vue2-leaflet").then((m) => m[componentName])
      : Promise.resolve({ render: (h) => h("div") });
  };
};

export default {
  components: {
    LMap: ImportLeafletComponent("LMap"),
    LTileLayer: ImportLeafletComponent("LTileLayer"),
    LTooltip: ImportLeafletComponent("LTooltip"),
    LPolygon: ImportLeafletComponent("LPolygon"),
    LControlScale: ImportLeafletComponent("LControlScale"),
    LMarker: ImportLeafletComponent("LMarker"),
    LIcon: ImportLeafletComponent("LIcon"),
  },
  props: {
    zones: {
      type: Array,
      default: () => [],
    },
    selectedZoneIds: {
      type: Array,
      default: () => [],
    },
    existingZoneIds: {
      type: Array,
      default: () => [],
    },
    removedZoneIds: {
      type: Array,
      default: () => [],
    },
    // ids of read-only reference zones (e.g. the person's active zones shown
    // on the waiting-list map) — rendered green and not clickable
    referenceZoneIds: {
      type: Array,
      default: () => [],
    },
    // drives the existing/selected zone colors so the map matches the
    // autocomplete pill color (e.g. "rating" yellow for waiting lists)
    colorType: {
      type: String,
      default: "success",
    },
    // color of the reference zones — the color of the field they belong to
    // (e.g. "success" green for active zones shown on the waiting-list map,
    // "rating" yellow for waiting-list zones shown on the active-zones map)
    referenceColorType: {
      type: String,
      default: "success",
    },
    // whether reference zones can be clicked/picked; false makes them read-only
    referenceSelectable: {
      type: Boolean,
      default: false,
    },
    // i18n key for the reference zone tooltip hint
    referenceHintKey: {
      type: String,
      default: "conductor.zones.mapSelector.referenceHint",
    },
    // the edited person's city, shown as a pin so the admin can see where they
    // are relative to the zones
    personCity: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const settingsStore = useSettingsStore();

    // existing/selected hues mirror the tailwind `bg-{type}` pill colors
    const THEME_COLORS = {
      success: { existing: "#22c55e", selected: "#16a34a" },
      rating: { existing: "#ffa800", selected: "#f2a000" },
    };

    const mapOptions = {
      zoomControl: true,
      attributionControl: false,
      minZoom: 7,
      maxZoom: 16,
      doubleClickZoom: false,
    };

    const zoom = ref(8);

    const countryCenterCoords = computed(
      () => settingsStore.countryCenterCoords
    );

    const zonesWithStyle = computed(() => {
      const theme = THEME_COLORS[props.colorType] ?? THEME_COLORS.success;
      const referenceTheme =
        THEME_COLORS[props.referenceColorType] ?? THEME_COLORS.success;
      return props.zones
        .filter((zone) => zone.latLng)
        .map((zone) => {
          const isExisting = props.existingZoneIds.includes(zone.id);
          const isRemoved = props.removedZoneIds.includes(zone.id);
          const isSelected = props.selectedZoneIds.includes(zone.id);
          const isReference = props.referenceZoneIds.includes(zone.id);
          let color = "#000000";
          if (isRemoved) {
            color = "#dd0000";
          } else if (isExisting) {
            color = theme.existing;
          } else if (isSelected) {
            color = theme.selected;
          } else if (isReference) {
            // colored by the field the reference zones belong to, so they match
            // that field's pills (green for active, yellow for waiting list)
            color = referenceTheme.existing;
          }
          return {
            ...zone,
            isExisting,
            isRemoved,
            isSelected,
            isReference,
            color,
          };
        });
    });

    const personCityLatLng = computed(() => {
      const city = props.personCity;
      if (!city || city.gpsLatitude == null || city.gpsLongitude == null) {
        return null;
      }
      return [city.gpsLatitude, city.gpsLongitude];
    });

    const onZoneClick = (zone) => {
      if (zone.isReference && !props.referenceSelectable) {
        return;
      }
      emit("toggle", zone);
    };

    const onMapReady = (mapObject) => {
      mapObject.setView(countryCenterCoords.value, zoom.value);
      setTimeout(() => mapObject.invalidateSize(), 250);
    };

    return {
      mapOptions,
      zoom,
      zonesWithStyle,
      personCityLatLng,
      markerIcon,
      onZoneClick,
      onMapReady,
      countryCenterCoords,
    };
  },
  fetchOnServer: false,
};
</script>
